<template>
  <div class="image-atom container mx-auto h-full text-center">
    <component
      :is="wrapperComponent"
      v-bind="wrapperProps"
      :class="{
        'h-full': true,
        'bg-transparent block w-full cursor-pointer border-0 p-0': openInLightbox,
      }"
      @click="handleClick"
    >
      <PtcPicture
        v-if="image?.asset"
        provider="sanity"
        format="webp"
        :src="image.asset._ref"
        :alt="
          AssertThat.warnOnly(() =>
            AssertThat.nonEmptyStringOrDefault(image?.altText, ' ', 'ImageAtom alt text empty')
          )
        "
        :img-attrs="{
          class: {
            'mx-auto p-0 h-full object-cover': true,
            'md:w-auto': !widthAttribute,
            'cursor-pointer': openInLightbox,
          },
          style: { width: widthAttribute },
        }"
        :modifiers="{
          crop: image.crop,
          hotspot: image.hotspot,
          ...(asIs ? { asIs: true } : {}),
        }"
        :loading="loading || 'lazy'"
      />
    </component>

    <span
      v-if="caption"
      :class="{
        'center-title mx-auto mt-2 block text-center text-13px italic leading-6 text-www-color-neutral-medium-dark': true,
      }"
      :style="{ width: widthAttribute, maxWidth: '100%' }"
    >
      <em>{{ caption }}</em>
    </span>
    <LightBoxPopup
      v-if="openInLightbox"
      v-bind="props"
      v-model="modalDisplay"
    />
  </div>
</template>

<script setup lang="ts">
import GeneralLink from '~/components/Molecules/HeaderComponents/GeneralLink.vue';
import PtcPicture from '~/components/Atoms/PtcPicture/PtcPicture.vue';
import LightBoxPopup from './LightBoxPopup.vue';
import type { ImageAtom } from '~/sanity/types';
import { sanitizeWidth } from '~/utils/width-util';
import * as AssertThat from '~/utils/AssertThat';

const props = defineProps<ImageAtom & { loading?: 'lazy' | 'eager' }>();
const { width, widthType } = props;
const widthAttribute = sanitizeWidth({ width, widthType });

const modalDisplay = ref(false);
const showModal = () => {
  modalDisplay.value = true;
};

const isLinkWrapper = computed(() => Boolean(props.clickable) && !props.openInLightbox);

const wrapperComponent = computed(() => (isLinkWrapper.value ? GeneralLink : 'div'));

const wrapperProps = computed<Record<string, unknown>>(() =>
  isLinkWrapper.value ? { text: '', link: props.clickable, _type: 'generalLink' } : {}
);

const handleClick = () => {
  if (props.openInLightbox) {
    showModal();
  }
};
</script>

<style lang="scss" scoped>
:global(body:has(.lightbox-overlay)) {
  overflow: hidden;
}
</style>
